import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { pushTenantParties, type TenantParty } from '@/api/tenantParties';
import { brand } from '@/theme/theme';

const EXAMPLE = JSON.stringify(
  [
    {
      name: 'Sunrise Medical Store',
      partyType: 'RETAILER',
      mobile: '9876543210',
      gstin: '29ABCDE1234F1Z5',
      address: '12 MG Road',
      city: 'Bengaluru',
    },
  ],
  null,
  2,
);

type PushedParty = TenantParty & { _status: 'created' | 'updated' };

export function TenantPartiesBulkPage() {
  const navigate = useNavigate();

  const [json, setJson]       = useState('');
  const [parseErr, setParseErr] = useState<string | null>(null);
  const [loading, setLoading]   = useState(false);
  const [result, setResult]     = useState<PushedParty[] | null>(null);
  const [apiErr, setApiErr]     = useState<string | null>(null);

  const handleSubmit = async () => {
    setParseErr(null);
    setApiErr(null);
    setResult(null);

    let rows: object[];
    try {
      const parsed = JSON.parse(json.trim());
      if (!Array.isArray(parsed)) throw new Error('Input must be a JSON array [ … ]');
      rows = parsed;
    } catch (e) {
      setParseErr((e as Error).message);
      return;
    }

    setLoading(true);
    try {
      const saved = await pushTenantParties(rows as Parameters<typeof pushTenantParties>[0]);
      setResult(saved as PushedParty[]);
      setJson('');
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      setApiErr(err?.response?.data?.message || 'Push failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          size="small"
          onClick={() => navigate(-1)}
          sx={{ fontSize: '0.75rem' }}
        >
          Back
        </Button>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h2" sx={{ color: brand.navy }}>Bulk Push Parties</Typography>
          <Typography variant="body2" sx={{ color: brand.inkSoft }}>
            Paste a JSON array of party objects to push them to your account.
            Existing rows matched by <code>(sourceApp, externalId)</code> are updated; all others are created.
          </Typography>
        </Box>
      </Stack>

      <Stack spacing={2}>
        {/* Input card */}
        <Card>
          <CardContent>
            <Stack spacing={1.5}>
              <Typography variant="subtitle2" sx={{ color: brand.navy }}>JSON Payload</Typography>
              <Typography variant="caption" sx={{ color: brand.inkSoft }}>
                Each object in the array may contain any field from the party schema
                (name, partyType, mobile, gstin, address, city, etc.).
              </Typography>

              <TextField
                multiline
                minRows={8}
                maxRows={20}
                placeholder={EXAMPLE}
                value={json}
                onChange={e => { setJson(e.target.value); setParseErr(null); }}
                error={!!parseErr}
                helperText={parseErr}
                inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.78rem' } }}
                fullWidth
              />

              {apiErr && <Alert severity="error">{apiErr}</Alert>}

              <Stack direction="row" justifyContent="flex-end">
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <UploadIcon />}
                  disabled={loading || !json.trim()}
                  onClick={handleSubmit}
                >
                  {loading ? 'Pushing…' : 'Push Parties'}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Result */}
        {result && (
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ color: brand.navy }}>Result</Typography>
                <Chip
                  size="small"
                  label={`${result.length} pushed`}
                  color="success"
                  sx={{ fontSize: '0.68rem', height: 20 }}
                />
              </Stack>

              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 620 }}>
                  <TableHead>
                    <TableRow>
                      {['ID', 'Name', 'Type', 'Mobile', 'GSTIN', 'City'].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.map(r => (
                      <TableRow key={r.id} hover>
                        <TableCell sx={{ fontSize: '0.72rem', fontFamily: 'monospace' }}>{r.id}</TableCell>
                        <TableCell sx={{ fontSize: '0.72rem' }}>{r.name}</TableCell>
                        <TableCell sx={{ fontSize: '0.72rem' }}>{r.partyType ?? '—'}</TableCell>
                        <TableCell sx={{ fontSize: '0.72rem' }}>{r.mobile ?? '—'}</TableCell>
                        <TableCell sx={{ fontSize: '0.72rem', fontFamily: 'monospace' }}>{r.gstin ?? '—'}</TableCell>
                        <TableCell sx={{ fontSize: '0.72rem' }}>{r.city ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </CardContent>
          </Card>
        )}
      </Stack>
    </Box>
  );
}
